import type React from 'react';
import type { Meta } from './steps/ValidationStep/types';
import type { StepState } from './steps/UploadFlow';
import { RsiTranslations } from './translations/RsiTranslations.types';
import { UserEventTracker } from './integrations/userEvents/UserEventsTypes';
import { Columns } from './steps/MatchColumnsStep/types';

export type RsiProps<T extends string> = {
  // Is modal visible.
  isOpen: boolean;
  // callback when RSI is closed before final submit
  onClose: () => void;
  // Field description for requested data
  fields: Fields<T>;
  // Runs after file upload step, receives and returns raw sheet data
  uploadStepHook?: (data: RawData[]) => Promise<RawData[]>;
  // Runs after header selection step, receives and returns raw sheet data
  selectHeaderStepHook?: (
    headerValues: RawData,
    data: RawData[]
  ) => Promise<{ headerValues: RawData; data: RawData[] }>;
  // Runs once before validation step, used for data mutations and if you want to change how columns were matched
  matchColumnsStepHook?: (table: Data<T>[], rawData: RawData[], columns: Columns<T>) => Promise<Data<T>[]>;
  // Runs after column matching and on entry change
  rowHook?: RowHook<T>;
  // Runs after column matching and on entry change
  tableHook?: TableHook<T>;
  // Function called after user finishes the flow. You can return a promise that will be awaited.
  onSubmit: (data: Result<T>, file: File) => void | Promise<any>;
  // Callback when RSI is closed after final submit
  onRestart?: () => void;
  // Allows submitting with errors. Default: true
  allowInvalidSubmit?: boolean;
  // Enable navigation in stepper component and show back button. Default: false
  isNavigationEnabled?: boolean;
  // Override translations for each label in RSI.
  translations?: DeepPartial<RsiTranslations>;
  // Specifies maximum number of rows for a single import
  maxRecords?: number;
  // Maximum upload filesize (in bytes)
  maxFileSize?: number;
  // Automatically map imported headers to specified fields if possible. Default: true
  autoMapHeaders?: boolean;
  // When field type is "select", automatically match values if possible. Default: false
  autoMapSelectValues?: boolean;
  // Headers matching accuracy: 1 for strict and up for more flexible matching
  autoMapDistance?: number;
  // Initial Step state to be rendered on load
  initialStepState?: StepState;
  // Sets SheetJS dateNF option. If date parsing is applied, date will be formatted e.g. "yyyy-mm-dd hh:mm:ss", "m/d/yy h:mm", 'mmm-yy', etc.
  dateFormat?: string;
  // Sets SheetJS "raw" option. If true, parsing will only be applied to xlsx date fields.
  parseRaw?: boolean;
  // Use for right-to-left (RTL) support
  rtl?: boolean;
  // Used to display the entity type in the final RSI confirmation modal.
  entityType?: string;
  // True if run from a specific screen or false if run from the Quick Actions widget.
  hasOnlyOneImportType?: boolean;
  // Callback to track user activity. Compatible with Mixpanel but can be used with any 3rd party lib.
  onTrackUserEvent?: UserEventTracker;
  // To help improve matching accuracy, exclude these prefixes when matching as they are not relevant. e.g. "Custom Field: "
  matcherIgnorePrefixes?: string[];
  // Called when the user clicks "Import Data", before error checking. Return modified data to proceed, or null to abort.
  preSubmitHook?: (data: (Data<T> & Meta)[]) => Promise<(Data<T> & Meta)[] | null>;
  // Render prop for content shown between the data grid and the footer on step 4. Receives the current row data.
  footerAlert?: (data: (Data<T> & Meta)[]) => React.ReactNode;
};

export type RawData = Array<string | undefined>;

export type Data<T extends string> = { [key in T]: string | string[] | boolean | undefined };

// Data model RSI uses for spreadsheet imports
export type Fields<T extends string> = DeepReadonly<Field<T>[]>;

export type Field<T extends string> = {
  // UI-facing field label
  label: string;
  // Field's unique identifier
  key: T;
  // UI-facing additional information displayed via tooltip and ? icon
  description?: string;
  // Alternate labels used for fields' auto-matching, e.g. "fname" -> "firstName"
  alternateMatches?: string[];
  // Validations used for field entries
  validations?: Validation[];
  // Field entry component, default: Input
  fieldType: Checkbox | Select | Input | MultiSelect;
  // UI-facing values shown to user as field examples pre-upload phase
  example?: string;
};

export type Checkbox = {
  type: 'checkbox';
  // Alternate values to be treated as booleans, e.g. {yes: true, no: false}
  booleanMatches?: { [key: string]: boolean };
};

export type Select = {
  type: 'select';
  valueSeparator?: string;
  // Options displayed in Select component
  options: SelectOption[];
  // Enables/Disables automatic sorting on Select fields [default: true]
  sort?: boolean;
  caseInsensitiveValueMatch?: boolean;
};

export type MultiSelect = {
  type: 'multiselect';
  valueSeparator?: string; // by default ","
  // Options displayed in Select component
  options: SelectOption[];
  // Enables/Disables automatic sorting on Select fields [default: true]
  sort?: boolean;
  caseInsensitiveValueMatch?: boolean;
};

export type SelectOption = {
  // UI-facing option label
  label: string;
  // Field entry matching criteria as well as select output
  value: string;
};

export type Input = {
  type: 'input';
};

export type Validation = RequiredValidation | UniqueValidation | RegexValidation;

export type RequiredValidation = {
  rule: 'required';
  errorMessage?: string;
  level?: ErrorLevel;
};

export type UniqueValidation = {
  rule: 'unique';
  allowEmpty?: boolean;
  errorMessage?: string;
  level?: ErrorLevel;
};

export type RegexValidation = {
  rule: 'regex';
  value: string;
  flags?: string;
  errorMessage: string;
  level?: ErrorLevel;
};

export type RowHook<T extends string> = (
  row: Data<T>,
  /* you can manually remove error by setting 'null' */
  addError: (fieldKey: T, error: Info | null, overrideRowIndex?: number) => void,
  table: Data<T>[]
) => Data<T> | Promise<Data<T>>;
export type TableHook<T extends string> = (
  table: Data<T>[],
  addError: (rowIndex: number, fieldKey: T, error: Info) => void
) => Data<T>[] | Promise<Data<T>[]>;

export type ErrorLevel = 'info' | 'warning' | 'error';

export type Info = {
  message: string;
  level: ErrorLevel;
};

export enum ErrorSources {
  Table = 'table',
  Row = 'row'
}

/*
   Source determines whether the error is from the full table or row validation
   Table validation is tableHook and "unique" validation
   Row validation is rowHook and all other validations
   it is used to determine if row.__errors should be updated or not depending on different validations
*/
export type InfoWithSource = Info & {
  source: ErrorSources;
};

export type Result<T extends string> = {
  validData: Data<T>[];
  invalidData: Data<T>[];
  all: (Data<T> & Meta)[];
};

export type DeeplyDefined<T> = Exclude<
  {
    [P in keyof T]-?: DeeplyDefined<T[P]>;
  },
  undefined | null
>;

export type ArrayType<Type extends readonly unknown[]> = Type extends (infer ElementType)[] ? ElementType : never;

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends Date
    ? Date
    : T[P] extends Function
      ? Function
      : T[P] extends any[]
        ? DeepPartial<ArrayType<T[P]>>[]
        : DeepPartial<T[P]>;
};

export type DeepReadonly<T> = {
  readonly [P in keyof T]: DeepReadonly<T[P]>;
};

export type Prettify<Type> = Type extends Function
  ? Type
  : Extract<
      {
        [Key in keyof Type]: Type[Key];
      },
      Type
    >;

export type MarkRequired<Type, Keys extends keyof Type> = Type extends Type
  ? Prettify<Type & Required<Pick<Type, Keys>>>
  : never;
